import { useCallback, useMemo, useState } from 'react';
import {
  HOLD_FALLBACK_SEC,
  REST_FALLBACK_SEC,
  type Exercise,
} from '@jackedlogs/shared';

import { saveExerciseTimerDefaults } from './exerciseTimerDefaults';
import type { DurableWriteFn, NotifyFn } from '../seams';

// Owns the in-Logger per-exercise timer-defaults sheet (ExerciseTimerSheet —
// rest + countdown/hold default in one interface). The timer gear ⚙ on the
// start affordance opens it against the active exercise, so a default can be
// set mid-workout without leaving for Settings. State lives here (not in a
// banner) so the Modal renders unconditionally in LoggerModals.
//
// Consolidation status (PARTIAL — do not overclaim): this hook and the
// Settings card (RestDefaultsSettingsCard) share ONE PATCH body via
// saveExerciseTimerDefaults, so those two can't drift. The rest-banner gear
// (useRestPicker.applyDefaultRest) still hand-rolls its own rest-only PATCH and
// is NOT yet unified — its clearing semantics differ (absolute-set vs
// clear-if-equal-to-global) and it writes only defaultRestSec, so folding it in
// is a non-trivial refactor. Tracked in next/consolidation.md; propose, don't
// sneak it in here.
type UseTimerDefaultsArgs = {
  catalog: Exercise[] | null;
  setCatalog: React.Dispatch<React.SetStateAction<Exercise[] | null>>;
  /** Profile globals — the seed + the "no override" fallback per field. */
  profileDefaultRestSec: number | null | undefined;
  profileDefaultDurationSec: number | null | undefined;
  t: (k: string, opts?: Record<string, unknown>) => string;
  durableWrite: DurableWriteFn;
  notify: NotifyFn;
  onSaved?: () => void;
};

export function useTimerDefaults({
  catalog,
  setCatalog,
  profileDefaultRestSec,
  profileDefaultDurationSec,
  t,
  durableWrite,
  notify,
  onSaved,
}: UseTimerDefaultsArgs) {
  const [slug, setSlug] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const restGlobal = profileDefaultRestSec ?? REST_FALLBACK_SEC;
  const holdGlobal = profileDefaultDurationSec ?? HOLD_FALLBACK_SEC;
  const exercise = useMemo(
    () => (slug ? (catalog?.find((x) => x.slug === slug) ?? null) : null),
    [slug, catalog],
  );

  const open = useCallback((s: string) => setSlug(s), []);
  const cancel = useCallback(() => setSlug(null), []);

  const save = useCallback(
    (restSec: number, holdSec: number) => {
      const target = slug;
      if (!target) return;
      void (async () => {
        setSaving(true);
        try {
          await saveExerciseTimerDefaults({
            slug: target,
            restSec,
            holdSec,
            restGlobal,
            holdGlobal,
            catalog,
            setCatalog,
            durableWrite,
            onSaved,
          });
          setSlug(null);
        } catch {
          notify(
            t('mobile.logger.restEditTitle'),
            t('mobile.logger.restEditError'),
          );
        } finally {
          setSaving(false);
        }
      })();
    },
    [slug, restGlobal, holdGlobal, catalog, setCatalog, t, durableWrite, notify, onSaved],
  );

  // Clearing the override = save both fields equal to the global (the shared
  // PATCH stores a value equal to the global as null → row falls back).
  const remove = useCallback(
    () => save(restGlobal, holdGlobal),
    [save, restGlobal, holdGlobal],
  );

  return { slug, exercise, restGlobal, holdGlobal, saving, open, cancel, save, remove };
}
